'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertTriangle, Info, ChevronRight, ChevronLeft, Activity, Heart, Clock, Shield, ArrowLeft } from 'lucide-react';
import BookingForm from '../booking-form';
import { useRTL } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger);

interface Question {
    id: number;
    question: string;
    options: { value: string; label: string; score: number }[];
}

interface Result {
    score: number;
    title: string;
    description: string;
    recommendation: string;
    additionalInfo?: string[];
}

export default function CandidateSection() {
    const { t, isLoading } = useTranslation('root-canal');
    const { isRTL } = useRTL();
    const [showQuiz, setShowQuiz] = useState(false);
    const [showBooking, setShowBooking] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [showResult, setShowResult] = useState(false);
    const [result, setResult] = useState<Result | null>(null);
    const [isMounted, setIsMounted] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);
    const formRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const getCandidateFactors = () => [
        {
            icon: Activity,
            label: t('candidate.factors.persistentPain.label'),
            description: t('candidate.factors.persistentPain.description')
        },
        {
            icon: Heart,
            label: t('candidate.factors.sensitivity.label'),
            description: t('candidate.factors.sensitivity.description')
        },
        {
            icon: Shield,
            label: t('candidate.factors.swollenGums.label'),
            description: t('candidate.factors.swollenGums.description')
        },
        {
            icon: Clock,
            label: t('candidate.factors.decay.label'),
            description: t('candidate.factors.decay.description')
        }
    ];

    const questions: Question[] = [
        {
            id: 1,
            question: t('candidate.quiz.questions.painSeverity.question'),
            options: [
                { value: "severe", label: t('candidate.quiz.questions.painSeverity.options.severe'), score: 10 },
                { value: "moderate", label: t('candidate.quiz.questions.painSeverity.options.moderate'), score: 8 },
                { value: "mild", label: t('candidate.quiz.questions.painSeverity.options.mild'), score: 6 },
                { value: "none", label: t('candidate.quiz.questions.painSeverity.options.none'), score: 2 }
            ]
        },
        {
            id: 2,
            question: t('candidate.quiz.questions.sensitivity.question'),
            options: [
                { value: "extreme", label: t('candidate.quiz.questions.sensitivity.options.extreme'), score: 10 },
                { value: "moderate", label: t('candidate.quiz.questions.sensitivity.options.moderate'), score: 8 },
                { value: "mild", label: t('candidate.quiz.questions.sensitivity.options.mild'), score: 5 },
                { value: "none", label: t('candidate.quiz.questions.sensitivity.options.none'), score: 2 }
            ]
        },
        {
            id: 3,
            question: t('candidate.quiz.questions.visibleDamage.question'),
            options: [
                { value: "darkening", label: t('candidate.quiz.questions.visibleDamage.options.darkening'), score: 9 },
                { value: "cavity", label: t('candidate.quiz.questions.visibleDamage.options.cavity'), score: 8 },
                { value: "crack", label: t('candidate.quiz.questions.visibleDamage.options.crack'), score: 7 },
                { value: "none", label: t('candidate.quiz.questions.visibleDamage.options.none'), score: 1 }
            ]
        },
        {
            id: 4,
            question: t('candidate.quiz.questions.gumSwelling.question'),
            options: [
                { value: "severe", label: t('candidate.quiz.questions.gumSwelling.options.severe'), score: 9 },
                { value: "moderate", label: t('candidate.quiz.questions.gumSwelling.options.moderate'), score: 7 },
                { value: "mild", label: t('candidate.quiz.questions.gumSwelling.options.mild'), score: 5 },
                { value: "none", label: t('candidate.quiz.questions.gumSwelling.options.none'), score: 1 }
            ]
        },
        {
            id: 5,
            question: t('candidate.quiz.questions.symptomDuration.question'),
            options: [
                { value: "weeks", label: t('candidate.quiz.questions.symptomDuration.options.weeks'), score: 10 },
                { value: "days", label: t('candidate.quiz.questions.symptomDuration.options.days'), score: 8 },
                { value: "recent", label: t('candidate.quiz.questions.symptomDuration.options.recent'), score: 6 },
                { value: "none", label: t('candidate.quiz.questions.symptomDuration.options.none'), score: 1 }
            ]
        }
    ];

    const getResult = (totalScore: number): Result => {
        if (totalScore >= 35) {
            return {
                score: totalScore,
                title: t('candidate.results.highPriority.title'),
                description: t('candidate.results.highPriority.description'),
                recommendation: t('candidate.results.highPriority.recommendation'),
                additionalInfo: [
                    t('candidate.results.highPriority.additionalInfo.painRelief'),
                    t('candidate.results.highPriority.additionalInfo.saveTooth'),
                    t('candidate.results.highPriority.additionalInfo.preventInfection')
                ]
            };
        } else if (totalScore >= 25) {
            return {
                score: totalScore,
                title: t('candidate.results.recommended.title'),
                description: t('candidate.results.recommended.description'),
                recommendation: t('candidate.results.recommended.recommendation'),
                additionalInfo: [
                    t('candidate.results.recommended.additionalInfo.earlyIntervention'),
                    t('candidate.results.recommended.additionalInfo.preventWorsening'),
                    t('candidate.results.recommended.additionalInfo.betterOutcomes')
                ]
            };
        } else if (totalScore >= 15) {
            return {
                score: totalScore,
                title: t('candidate.results.monitor.title'),
                description: t('candidate.results.monitor.description'),
                recommendation: t('candidate.results.monitor.recommendation'),
                additionalInfo: [
                    t('candidate.results.monitor.additionalInfo.preventiveCare'),
                    t('candidate.results.monitor.additionalInfo.earlyDetection'),
                    t('candidate.results.monitor.additionalInfo.multipleOptions')
                ]
            };
        } else {
            return {
                score: totalScore,
                title: t('candidate.results.lowRisk.title'),
                description: t('candidate.results.lowRisk.description'),
                recommendation: t('candidate.results.lowRisk.recommendation'),
                additionalInfo: [
                    t('candidate.results.lowRisk.additionalInfo.regularCheckups'),
                    t('candidate.results.lowRisk.additionalInfo.goodHygiene'),
                    t('candidate.results.lowRisk.additionalInfo.watchSymptoms')
                ]
            };
        }
    };

    const handleAnswer = (value: string) => {
        setAnswers(prev => ({ ...prev, [currentQuestion]: value }));
    };

    const nextQuestion = () => {
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(prev => prev + 1);
        } else {
            calculateResult();
        }
    };

    const prevQuestion = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(prev => prev - 1);
        }
    };

    const calculateResult = () => {
        const totalScore = questions.reduce((sum, question) => {
            const answer = answers[question.id - 1];
            const option = question.options.find(opt => opt.value === answer);
            return sum + (option?.score || 0);
        }, 0);

        setResult(getResult(totalScore));
        setShowResult(true);
    };

    const handleConsultationClick = () => {
        setShowBooking(true);
    };

    const handleBackToResults = () => {
        setShowBooking(false);
    };

    const resetAll = () => {
        setShowQuiz(false);
        setShowBooking(false);
        setShowResult(false);
        setCurrentQuestion(0);
        setAnswers({});
        setResult(null);
    };

    const getResultColor = (title: string) => {
        if (title.includes("High Priority")) return "from-red-500 to-red-600";
        if (title.includes("Recommended")) return "from-orange-500 to-orange-600";
        if (title.includes("Monitor")) return "from-yellow-500 to-yellow-600";
        return "from-green-500 to-green-600";
    };

    const getResultIcon = (title: string) => {
        if (title.includes("High Priority")) return <AlertTriangle className="w-8 h-8 text-white" />;
        if (title.includes("Recommended")) return <Info className="w-8 h-8 text-white" />;
        if (title.includes("Monitor")) return <Clock className="w-8 h-8 text-white" />;
        return <CheckCircle className="w-8 h-8 text-white" />;
    };

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(sectionRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: "top 80%",
                        toggleActions: "play none none reverse"
                    }
                }
            );
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section id="candidate" ref={sectionRef} className="py-20 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
            <div className="container">
                <div className="text-center mb-16">
                    {!isMounted || !!isLoading ? (
                        <div className="animate-pulse">
                            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-4 max-w-2xl mx-auto"></div>
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-8 max-w-3xl mx-auto"></div>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif mb-6">
                                {t('candidate.title.part1')} <span className="text-gold">{t('candidate.title.part2')}</span>
                            </h2>
                            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
                                {t('candidate.subtitle')}
                            </p>
                        </>
                    )}
                </div>

                <div className="max-w-4xl mx-auto">
                    {!isMounted || !!isLoading ? (
                        <div className="animate-pulse">
                            <div className="grid md:grid-cols-2 gap-6 mb-8">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="p-6 bg-gray-200 dark:bg-gray-700 rounded-xl">
                                        <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                    </div>
                                ))}
                            </div>
                            <div className="text-center">
                                <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl w-64 mx-auto"></div>
                            </div>
                        </div>
                    ) : (
                        <AnimatePresence mode="wait">
                            {!showQuiz && !showBooking ? (
                                <motion.div
                                    key="eligibility-info"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="space-y-8"
                                >
                                    {/* Eligibility factors */}
                                    <div className="grid md:grid-cols-2 gap-6">
                                        {getCandidateFactors().map((factor, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                            className={`flex items-start gap-4 p-6 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 hover:border-gold transition-colors duration-300 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                                        >
                                            <div className={`${isRTL ? 'text-right' : 'text-left'} flex-1`}>
                                                <h3 className={`font-semibold text-navy dark:text-white mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                                    {factor.label}
                                                </h3>
                                                <p className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-left'}`}>
                                                    {factor.description}
                                                </p>
                                            </div>
                                            <div className="flex-shrink-0">
                                                <factor.icon className="w-6 h-6 text-gold" />
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* CTA to start quiz */}
                                <div className="text-center">
                                    <Button
                                        onClick={() => setShowQuiz(true)}
                                        size="lg"
                                        className={`bg-gold hover:bg-gold-600 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                                    >
                                        <ChevronRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                                        <span>{t('candidate.quiz.startButton')}</span>
                                    </Button>
                                </div>
                            </motion.div>
                        ) : showQuiz && !showResult && !showBooking ? (
                            <motion.div
                                key="quiz"
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                className="bg-white dark:bg-slate-800 rounded-2xl p-8 border border-slate-200 dark:border-slate-600 shadow-xl"
                            >
                                {/* Quiz header */}
                                <div className="flex items-center justify-between mb-8">
                                    <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={resetAll}
                                            className="border-gold text-gold hover:bg-gold hover:text-white"
                                        >
                                            <ArrowLeft className={`w-4 h-4 ${isRTL ? 'order-last rotate-180' : ''}`} />
                                            {t('candidate.quiz.backButton')}
                                        </Button>
                                        <h3 className="text-xl font-bold text-navy dark:text-white">
                                            {t('candidate.quiz.title')}
                                        </h3>
                                    </div>
                                    <div className="text-sm text-slate-600 dark:text-slate-300">
                                        {currentQuestion + 1} {t('candidate.quiz.progressText')} {questions.length}
                                    </div>
                                </div>

                                {/* Progress bar */}
                                <div className="mb-8">
                                    <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-2">
                                        <div
                                            className="bg-gold h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Question */}
                                <div className="mb-8">
                                    <h4 className="text-lg font-semibold text-navy dark:text-white mb-6">
                                        {questions[currentQuestion].question}
                                    </h4>
                                    <div className="space-y-3">
                                        {questions[currentQuestion].options.map((option) => (
                                            <label
                                                key={option.value}
                                                className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${answers[currentQuestion] === option.value
                                                    ? 'border-gold bg-gold/5'
                                                    : 'border-slate-200 dark:border-slate-600 hover:border-gold/50'
                                                    }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name={`question-${currentQuestion}`}
                                                    value={option.value}
                                                    checked={answers[currentQuestion] === option.value}
                                                    onChange={() => handleAnswer(option.value)}
                                                    className="sr-only"
                                                />
                                                <span className={`text-navy dark:text-white flex-1 ${isRTL ? 'text-right order-1' : 'order-2'}`}>{option.label}</span>
                                                <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${isRTL ? 'order-2' : 'order-1'} ${answers[currentQuestion] === option.value
                                                    ? 'border-gold bg-gold'
                                                    : 'border-slate-200 dark:border-slate-600'
                                                    }`}>
                                                    {answers[currentQuestion] === option.value && (
                                                        <div className="w-full h-full rounded-full bg-white scale-50"></div>
                                                    )}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Navigation */}
                                <div className="flex justify-between">
                                    <Button
                                        variant="outline"
                                        onClick={prevQuestion}
                                        disabled={currentQuestion === 0}
                                        className="border-gold text-gold hover:bg-gold hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft className={`w-4 h-4 ${isRTL ? 'order-last rotate-180' : ''}`} />
                                        {t('candidate.quiz.previousButton')}
                                    </Button>
                                    <Button
                                        onClick={nextQuestion}
                                        disabled={!answers[currentQuestion]}
                                        className="bg-gold hover:bg-gold-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {currentQuestion === questions.length - 1 ? t('candidate.quiz.getResultsButton') : t('candidate.quiz.nextButton')}
                                        <ChevronRight className={`w-4 h-4 ${isRTL ? 'order-first rotate-180' : ''}`} />
                                    </Button>
                                </div>
                            </motion.div>
                        ) : showResult && !showBooking ? (
                            <motion.div
                                key="result"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="bg-white dark:bg-slate-800 rounded-2xl p-8 border border-slate-200 dark:border-slate-600 shadow-xl"
                            >
                                {result && (
                                    <div className="text-center">
                                        {/* Result header */}
                                        <div className={`w-20 h-20 bg-gradient-to-br ${getResultColor(result.title)} rounded-full flex items-center justify-center mx-auto mb-6`}>
                                            {getResultIcon(result.title)}
                                        </div>

                                        <h3 className="text-2xl font-bold text-navy dark:text-white mb-4">
                                            {result.title}
                                        </h3>

                                        <p className={`text-slate-600 dark:text-slate-300 mb-6 leading-relaxed ${isRTL ? 'text-right' : 'text-center'}`}>
                                            {result.description}
                                        </p>

                                        <p className={`text-navy dark:text-white font-semibold mb-8 ${isRTL ? 'text-right' : 'text-center'}`}>
                                            {result.recommendation}
                                        </p>

                                        {result.additionalInfo && (
                                            <div className="mb-8">
                                                <h4 className="font-semibold text-navy dark:text-white mb-4">{t('candidate.results.expectationTitle')}</h4>
                                                <div className="grid md:grid-cols-3 gap-4">
                                                    {result.additionalInfo.map((info, index) => (
                                                        <div key={index} className={`flex items-center gap-2 p-3 bg-gold/10 rounded-lg ${isRTL ? 'justify-end flex-row-reverse text-right' : 'justify-center'}`}>
                                                            <span className={`text-sm text-navy dark:text-white ${isRTL ? 'text-right' : 'text-center'}`}>{info}</span>
                                                            <CheckCircle className="w-5 h-5 text-gold flex-shrink-0" />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Action buttons */}
                                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                            <Button
                                                onClick={handleConsultationClick}
                                                size="lg"
                                                className="bg-gold hover:bg-gold-600 text-white px-8 py-4 font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300"
                                            >
                                                {t('candidate.results.scheduleButton')}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={resetAll}
                                                className="border-gold text-gold hover:bg-gold hover:text-white px-8 py-4 font-semibold rounded-xl"
                                            >
                                                {t('candidate.results.retakeButton')}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        ) : showBooking ? (
                            <motion.div
                                key="booking"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                ref={formRef}
                                className="bg-white dark:bg-slate-800 rounded-2xl p-8 border border-slate-200 dark:border-slate-600 shadow-xl"
                            >
                                <div className="flex items-center justify-between mb-8">
                                    <h3 className="text-xl font-bold text-navy dark:text-white">
                                        {t('candidate.booking.title')}
                                    </h3>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleBackToResults}
                                        className="border-gold text-gold hover:bg-gold hover:text-white"
                                    >
                                        <ArrowLeft className={`w-4 h-4 ${isRTL ? 'order-last rotate-180' : ''}`} />
                                        {t('candidate.booking.backToResults')}
                                    </Button>
                                </div>
                                <BookingForm treatmentType="Root Canal" />
                            </motion.div>
                        ) : null}
                        </AnimatePresence>
                    )}
                </div>
            </div>
        </section>
    );
} 